import { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Alert,
  Badge,
  Card,
  SimpleGrid,
  ActionIcon,
  Loader,
  ThemeIcon,
} from '@mantine/core';
import {
  IconFileImport,
  IconTrash,
  IconAlertCircle,
  IconCheck,
  IconClockHour4,
  IconCalendar,
  IconBolt,
  IconInfoCircle,
  IconEye,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useGetImportSummary } from '../../hooks/useGetImportSummary';
import { useDeleteHourlyData } from '../../hooks/useDeleteHourlyData';
import { useGetAvailableYears } from '../../hooks/useGetAvailableYears';
import { MonthDetailModal } from './MonthDetailModal';
import { YearSelector } from './YearSelector';
import { isAuthError } from '../../../../utils/authErrors';

const MONTH_NAMES = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

/**
 * Calculate total hours in a month
 * @param year - Year (for leap year calculation)
 * @param month - Month number (1-12)
 * @returns Total hours in the month (720-744)
 */
function getHoursInMonth(year: number, month: number): number {
  const daysInMonth = new Date(year, month, 0).getDate();
  return daysInMonth * 24;
}

export function ImportedDataList() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<{ yearMonth: string; name: string } | null>(null);

  // Generate available years: current year ± 2
  const availableYears = useMemo(() => {
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y++) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const { data: importSummary, loading, error, refetch } = useGetImportSummary(selectedYear);
  const { deleteHourlyData, loading: deleteLoading } = useDeleteHourlyData();
  const { yearsWithData, refetch: refetchYears } = useGetAvailableYears();

  // Auto-select the most recent year that has data (avoids showing empty state for current year)
  useEffect(() => {
    if (yearsWithData.length > 0) {
      const mostRecentYear = Math.max(...yearsWithData);
      setSelectedYear(mostRecentYear);
    }
  }, [yearsWithData]);

  const handleDelete = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-');
    const monthName = MONTH_NAMES[parseInt(month, 10) - 1];

    modals.openConfirmModal({
      title: 'Potwierdź usunięcie importu',
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć zaimportowane dane dla miesiąca <strong>{monthName} {year}</strong>?
          <br />
          <br />
          Zaimportowane dane zostaną zastąpione danymi <strong>oszacowanymi</strong> (wygenerowanymi na podstawie innych miesięcy).
          <br />
          <br />
          Miesiąc pozostanie na liście ze statusem <strong>"OSZACOWANE"</strong>.
        </Text>
      ),
      labels: { confirm: 'Zamień na oszacowanie', cancel: 'Anuluj' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          const result = await deleteHourlyData({ yearMonth });

          notifications.show({
            title: 'Sukces',
            message: `Zamieniono import na oszacowanie dla ${monthName} ${year} (${result.deletedCount || 0} godzin)`,
            color: 'green',
            icon: <IconCheck size={18} />,
          });

          // Refresh the list and available years
          await refetch(selectedYear);
          await refetchYears();
        } catch (err) {
          notifications.show({
            title: isAuthError(err) ? 'Sesja wygasła' : 'Błąd',
            message: isAuthError(err) ? 'Zaloguj się ponownie.' : (err instanceof Error ? err.message : 'Nie udało się usunąć danych'),
            color: isAuthError(err) ? 'orange' : 'red',
            icon: <IconAlertCircle size={18} />,
          });
        }
      },
    });
  };

  const handleDeleteAll = () => {
    if (!importSummary || importSummary.imports.length === 0) return;

    const totalMonths = importSummary.imports.length;

    modals.openConfirmModal({
      title: 'Potwierdź usunięcie wszystkich danych',
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć <strong>WSZYSTKIE</strong> zaimportowane dane godzinowe dla roku{' '}
          {importSummary.year}?
          <br />
          <br />
          Zostaną usunięte dane dla <strong>{totalMonths}</strong>{' '}
          {totalMonths === 1 ? 'miesiąca' : totalMonths < 5 ? 'miesięcy' : 'miesięcy'}.
          <br />
          <br />
          <strong style={{ color: 'var(--mantine-color-red-6)' }}>
            Ta operacja jest nieodwracalna!
          </strong>
        </Text>
      ),
      labels: { confirm: 'Usuń wszystkie dane', cancel: 'Anuluj' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        let deletedCount = 0;
        let failedCount = 0;
        let authFailed = false;

        // Delete each month sequentially
        for (const imp of importSummary.imports) {
          try {
            await deleteHourlyData({ yearMonth: imp.yearMonth });
            deletedCount++;
          } catch (err) {
            if (isAuthError(err)) {
              authFailed = true;
              break;
            }
            failedCount++;
          }
        }

        // Refresh the list and available years
        await refetch(selectedYear);
        await refetchYears();

        // Show summary notification
        if (authFailed) {
          notifications.show({
            title: 'Sesja wygasła',
            message: 'Zaloguj się ponownie.',
            color: 'orange',
            icon: <IconAlertCircle size={18} />,
          });
        } else if (failedCount === 0) {
          notifications.show({
            title: 'Sukces',
            message: `Usunięto wszystkie dane (${deletedCount} ${deletedCount === 1 ? 'miesiąc' : 'miesięcy'})`,
            color: 'green',
            icon: <IconCheck size={18} />,
          });
        } else {
          notifications.show({
            title: 'Częściowe powodzenie',
            message: `Usunięto ${deletedCount} ${deletedCount === 1 ? 'miesiąc' : 'miesięcy'}, nie udało się usunąć ${failedCount}`,
            color: 'orange',
            icon: <IconAlertCircle size={18} />,
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <Paper p="md" withBorder radius="md" shadow="sm">
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Ładowanie zaimportowanych danych...</Text>
        </Group>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={18} />} title="Błąd ładowania danych">
        <Text size="sm">{error}</Text>
      </Alert>
    );
  }

  if (!importSummary || importSummary.totalMonths === 0) {
    return (
      <Paper p="md" withBorder radius="md" shadow="sm">
        <Stack gap="md" align="center" py="xl">
          <ThemeIcon size={64} radius="xl" color="gray" variant="light">
            <IconFileImport size={32} />
          </ThemeIcon>
          <Stack ta="center" gap={4}>
            <Text size="lg" fw={500}>
              Brak zaimportowanych danych
            </Text>
            <Text size="sm" c="dimmed">
              Nie masz jeszcze żadnych zaimportowanych danych godzinowych dla roku {selectedYear}.
              <br />
              Użyj formularza powyżej, aby zaimportować dane z pliku CSV.
            </Text>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  // Calculate imported vs extrapolated counts
  const importedCount = importSummary.imports.filter((imp) => !imp.isExtrapolated).length;
  const extrapolatedCount = importSummary.imports.filter((imp) => imp.isExtrapolated).length;

  // Calculate total kWh consumption
  const importedKWh = importSummary.imports
    .filter((imp) => !imp.isExtrapolated)
    .reduce((sum, imp) => sum + imp.totalConsumption, 0);
  const extrapolatedKWh = importSummary.imports
    .filter((imp) => imp.isExtrapolated)
    .reduce((sum, imp) => sum + imp.totalConsumption, 0);
  const totalKWh = importedKWh + extrapolatedKWh;

  return (
    <Paper p="md" withBorder radius="md" shadow="sm">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Title order={3} size="h4" mb={4}>
              <IconFileImport
                size={20}
                style={{ marginRight: 8, verticalAlign: 'middle' }}
              />
              Dane z Twojego licznika
            </Title>
            <Text size="sm" c="dimmed">
              Rok {importSummary.year} - zaimportowano {importedCount}{' '}
              {importedCount === 1 ? 'miesiąc' : 'miesięcy'}, oszacowano {extrapolatedCount}{' '}
              {extrapolatedCount === 1 ? 'miesiąc' : 'miesięcy'}
            </Text>
          </div>
          <Group gap="xs">
            <Badge size="lg" color="green" variant="light">
              {importSummary.totalMonths} {importSummary.totalMonths === 1 ? 'miesiąc' : 'miesięcy'}
            </Badge>
            {importSummary.totalMonths > 0 && (
              <Button
                color="red"
                variant="subtle"
                size="xs"
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteAll}
                loading={deleteLoading}
                disabled={deleteLoading}
              >
                Usuń wszystkie
              </Button>
            )}
          </Group>
        </Group>

        {/* Year Selector */}
        <YearSelector
          selectedYear={selectedYear}
          availableYears={availableYears}
          yearsWithData={yearsWithData}
          onChange={setSelectedYear}
          disabled={loading || deleteLoading}
        />

        {/* kWh Summary */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <Card padding="sm" withBorder bg="green.0">
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" color="green" variant="light">
                <IconBolt size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Zaimportowano</Text>
            </Group>
            <Text size="lg" fw={700} c="green.7">
              {importedKWh.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} kWh
            </Text>
            <Text size="xs" c="dimmed">
              z {importedCount} {importedCount === 1 ? 'miesiąca' : 'miesięcy'}
            </Text>
          </Card>

          <Card padding="sm" withBorder bg="orange.0">
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" color="orange" variant="light">
                <IconClockHour4 size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Oszacowano</Text>
            </Group>
            <Text size="lg" fw={700} c="orange.7">
              {extrapolatedKWh.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} kWh
            </Text>
            <Text size="xs" c="dimmed">
              z {extrapolatedCount} {extrapolatedCount === 1 ? 'miesiąca' : 'miesięcy'}
            </Text>
          </Card>

          <Card padding="sm" withBorder bg="blue.0">
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" color="blue" variant="light">
                <IconBolt size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Razem roczne</Text>
            </Group>
            <Text size="lg" fw={700} c="blue.7">
              {totalKWh.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} kWh
            </Text>
            <Text size="xs" c="dimmed">
              średnio {(totalKWh / 12).toLocaleString('pl-PL', { maximumFractionDigits: 0 })} kWh/mies.
            </Text>
          </Card>
        </SimpleGrid>

        {/* Info Alert */}
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
          <Stack gap="sm">
            <Text size="sm">
              Zaimportowane dane godzinowe są wykorzystywane w kalkulatorach przy wyborze wzorca zużycia{' '}
              <strong>"Z raportu Tauron"</strong>.
            </Text>
            <Group gap="xs">
              <Button
                component="a"
                href="/calculator"
                size="xs"
                variant="light"
                color="blue"
              >
                Kalkulator Szczegółowy
              </Button>
              <Button
                component="a"
                href="/pro-calculator"
                size="xs"
                variant="light"
                color="violet"
              >
                Kalkulator Profesjonalny
              </Button>
            </Group>
          </Stack>
        </Alert>

        {/* Import List */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {importSummary.imports
            .sort((a, b) => {
              // Sort by month number (yearMonth format: "YYYY-MM")
              const monthA = parseInt(a.yearMonth.split('-')[1], 10);
              const monthB = parseInt(b.yearMonth.split('-')[1], 10);
              return monthA - monthB;
            })
            .map((imp) => {
              const [year, month] = imp.yearMonth.split('-');
              const monthNum = parseInt(month, 10);
              const monthName = MONTH_NAMES[monthNum - 1];
              const importDate = new Date(imp.importDate);
              const formattedDate = importDate.toLocaleDateString('pl-PL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });
              const formattedTime = importDate.toLocaleTimeString('pl-PL', {
                hour: '2-digit',
                minute: '2-digit',
              });

              // Calculate total hours in this month
              const totalHours = getHoursInMonth(parseInt(year, 10), monthNum);
              const importedHours = imp.hoursCount;

            return (
              <Card key={imp.yearMonth} padding="md" withBorder>
                <Stack gap="sm">
                  {/* Month Header */}
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs">
                      <ThemeIcon
                        size="md"
                        radius="xl"
                        color={imp.isExtrapolated ? 'orange' : 'green'}
                        variant="light"
                      >
                        {imp.isExtrapolated ? (
                          <IconClockHour4 size={16} />
                        ) : (
                          <IconCheck size={16} />
                        )}
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={600}>
                          {monthName} {year}
                        </Text>
                        <Badge
                          size="xs"
                          color={imp.isExtrapolated ? 'orange' : 'green'}
                          variant="light"
                        >
                          {imp.isExtrapolated ? 'Oszacowane' : 'Zaimportowane'}
                        </Badge>
                      </div>
                    </Group>
                    {!imp.isExtrapolated && (
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => handleDelete(imp.yearMonth)}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    )}
                  </Group>

                  {/* Stats */}
                  <Stack gap="xs">
                    <Group gap="xs">
                      <IconClockHour4 size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                      <Text size="xs" c="dimmed">
                        {imp.isExtrapolated
                          ? `${totalHours.toLocaleString('pl-PL')} godzin`
                          : `${importedHours.toLocaleString('pl-PL')} / ${totalHours.toLocaleString('pl-PL')} godzin`}
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <IconBolt size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                      <Text size="xs" c="dimmed">
                        {imp.totalConsumption.toLocaleString('pl-PL', {
                          maximumFractionDigits: 2,
                        })}{' '}
                        kWh
                      </Text>
                    </Group>
                    {!imp.isExtrapolated && (
                      <Group gap="xs">
                        <IconCalendar size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                        <Text size="xs" c="dimmed">
                          Zaimportowano: {formattedDate} {formattedTime}
                        </Text>
                      </Group>
                    )}
                  </Stack>

                  {/* View Details Button */}
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconEye size={14} />}
                    fullWidth
                    onClick={() => setSelectedMonth({ yearMonth: imp.yearMonth, name: `${monthName} ${year}` })}
                  >
                    Zobacz szczegóły
                  </Button>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      </Stack>

      {/* Month Detail Modal */}
      <MonthDetailModal
        opened={selectedMonth !== null}
        onClose={() => setSelectedMonth(null)}
        yearMonth={selectedMonth?.yearMonth || ''}
        monthName={selectedMonth?.name || ''}
      />
    </Paper>
  );
}
